import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import AppIcon from "../../../components/ui/AppIcon";
import { useChat } from "../../../context/ChatContext";
import { useAuth } from "../../../context/AuthContext";
import { storeChats, uploadFile } from "../../../services/chatServices";
import { sendChannelMessage } from "../../../services/channelService";
import { deleteDraft as deleteDraftAPI } from "../../../services/draftService";

export default function DraftsModal({ onClose }) {
  const { user } = useAuth();
  const { userDrafts, channels, directMessages, activeChat, selectChat, refreshData } = useChat();

  console.log("🎨 DraftsModal props: onClose", onClose);
  console.log("📚 userDrafts from context:", userDrafts);
  console.log("📚 channels:", channels);
  console.log("📚 directMessages:", directMessages);
  const [activeTab, setActiveTab] = useState("drafts"); // 'drafts' | 'scheduled'
  const [sendingDraftId, setSendingDraftId] = useState(null);

  // Separate drafts by scheduled status
  const unsentDrafts = userDrafts.filter(d => !d.scheduledAt);
  const scheduledDrafts = userDrafts.filter(d => d.scheduledAt);

  // Get chat name/avatar for a draft
  const getChatInfo = useCallback((draft) => {
    const { chatId, chatType } = draft;
    if (chatType === 'channel') {
      const channel = channels.find(c => (c._id || c.id).toString() === chatId.toString());
      if (channel) return { name: channel.name, avatar: null, type: 'channel', data: channel };
    } else {
      const dm = directMessages.find(d => (d._id || d.id).toString() === chatId.toString());
      if (dm) return { name: dm.name || dm.displayName, avatar: dm.profilePhoto, type: 'dm', data: dm };
    }
    return { name: 'Unknown Chat', avatar: null, type: null, data: null };
  }, [channels, directMessages]);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Load draft into chat
  const handleLoadDraft = (draft) => {
    const { chatId, chatType } = draft;
    let chatData = null;
    if (chatType === 'channel') {
      chatData = channels.find(c => (c._id || c.id).toString() === chatId.toString());
    } else {
      chatData = directMessages.find(d => (d._id || d.id).toString() === chatId.toString());
    }
    if (chatData) {
      selectChat(chatType, chatData);
      onClose();
    }
  };

  // Send draft now (skip schedule)
  const handleSendNow = async (draft) => {
    if (!user) return;
    const { chatId, chatType, content } = draft;
    try {
      setSendingDraftId(draft._id);
      // This will actually send the message immediately
      if (chatType === 'channel') {
        const channelId = chatId;
        const userId = user._id || user.id;
        const reqPayload = {
          channel: channelId,
          sender: userId,
          text: content,
          type: "text",
        };
        await sendChannelMessage(channelId, reqPayload);
      } else {
        const receiverId = chatId;
        const receiver = directMessages.find(d => (d._id || d.id).toString() === receiverId.toString());
        if (!receiver) throw new Error("Receiver not found");
        const sortedIds = [user._id || user.id, receiverId].sort();
        const reqPayload = {
          user1Id: sortedIds[0],
          user2Id: sortedIds[1],
          messages: [{
            sender: user.name,
            receiver: receiver.name || receiver.displayName,
            type: "text",
            message: content,
            timestamp: new Date().toISOString(),
          }]
        };
        await storeChats(reqPayload);
      }
      // Delete draft after sending
      await deleteDraftAPI(chatId);
      // Refresh data
      await refreshData();
    } catch (error) {
      console.error("Error sending draft now:", error);
      toast.error("Failed to send draft");
    } finally {
      setSendingDraftId(null);
    }
  };

  // Delete draft
  const handleDelete = (draft) => {
    toast((t) => (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-semibold">Delete this draft?</p>
        <div className="flex gap-2 mt-1">
          <button
            onClick={() => { toast.dismiss(t.id); confirmDelete(draft); }}
            className="flex-1 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-bold transition-colors"
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="flex-1 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const confirmDelete = async (draft) => {
    try {
      await deleteDraftAPI(draft.chatId);
      await refreshData();
      toast.success("Draft deleted");
    } catch (error) {
      console.error("Error deleting draft:", error);
      toast.error("Failed to delete draft");
    }
  };

  // Render a single draft item
  const renderDraftItem = (draft) => {
    const chatInfo = getChatInfo(draft);
    const isScheduled = !!draft.scheduledAt;
    const truncatedContent = draft.content
      ? draft.content.length > 100 ? draft.content.substring(0, 100) + '...' : draft.content
      : '(empty)';

    return (
      <div
        key={draft._id}
        className="group flex items-start gap-4 p-4 rounded-2xl bg-app-surface border border-border-subtle hover:bg-black/5 transition-all shadow-subtle hover:scale-[1.01]"
      >
        {/* Avatar or Channel icon */}
        <div className="flex-shrink-0 mt-0.5">
          {chatInfo.avatar ? (
            <img src={chatInfo.avatar} className="w-10 h-10 rounded-xl object-cover border border-border-subtle" alt={chatInfo.name} />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
              <AppIcon name="HashtagIcon" size={18} className="text-primary" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[14px] font-bold text-text-primary truncate">{chatInfo.name}</p>
            {isScheduled && (
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 uppercase tracking-widest">
                {formatDate(draft.scheduledAt)}
              </span>
            )}
          </div>
          <p className="text-[13px] text-text-secondary leading-relaxed line-clamp-3">
            {truncatedContent}
          </p>
          {draft.scheduledAt && (
            <p className="text-[10px] font-bold text-primary/70 mt-2 uppercase tracking-widest">
              Scheduled for {formatDate(draft.scheduledAt)}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">
          <button
            onClick={() => handleLoadDraft(draft)}
            title="Open in chat"
            className="p-2 rounded-xl bg-app-surface hover:bg-primary/10 text-text-muted hover:text-primary border border-border-subtle hover:border-primary/30 transition-all shadow-subtle"
          >
            <AppIcon name="PencilSquareIcon" size={16} />
          </button>
          {isScheduled && (
            <button
              onClick={() => handleSendNow(draft)}
              disabled={sendingDraftId === draft._id}
              title="Send now"
              className="p-2 rounded-xl bg-app-surface hover:bg-green-500/10 text-text-muted hover:text-green-500 border border-border-subtle hover:border-green-500/30 transition-all shadow-subtle"
            >
              {sendingDraftId === draft._id ? (
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              ) : (
                <AppIcon name="PaperAirplaneIcon" size={16} />
              )}
            </button>
          )}
          <button
            onClick={() => handleDelete(draft)}
            title="Delete"
            className="p-2 rounded-xl bg-app-surface hover:bg-red-500/10 text-text-muted hover:text-red-500 border border-border-subtle hover:border-red-500/30 transition-all shadow-subtle"
          >
            <AppIcon name="TrashIcon" size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4 animate-fade-in">
      <div className="w-full max-w-2xl bg-app-surface border border-border-subtle rounded-3xl shadow-modal flex flex-col max-h-[85vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border-subtle bg-app-bg/30">
          <div>
            <h2 className="text-xl font-bold text-text-primary tracking-tight">Drafts</h2>
            <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mt-1 opacity-60">
              {unsentDrafts.length} unsent • {scheduledDrafts.length} scheduled
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl text-text-muted hover:text-text-primary hover:bg-black/5 border border-transparent transition-all shadow-subtle"
          >
            <AppIcon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-subtle px-6 bg-app-bg/10">
          <button
            onClick={() => setActiveTab("drafts")}
            className={`px-6 py-4 text-[12px] font-bold uppercase tracking-widest border-b-2 transition-all ${activeTab === "drafts"
              ? "border-primary text-primary"
              : "border-transparent text-text-muted hover:text-text-primary"
              }`}
          >
            Unsent ({unsentDrafts.length})
          </button>
          <button
            onClick={() => setActiveTab("scheduled")}
            className={`px-6 py-4 text-[12px] font-bold uppercase tracking-widest border-b-2 transition-all ${activeTab === "scheduled"
              ? "border-primary text-primary"
              : "border-transparent text-text-muted hover:text-text-primary"
              }`}
          >
            Scheduled ({scheduledDrafts.length})
          </button>
        </div>

        {/* Drafts List */}
        <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
          {activeTab === "drafts" ? (
            unsentDrafts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
                  <AppIcon name="PencilSquareIcon" size={28} className="text-[var(--text-muted)]" />
                </div>
                <p className="text-[var(--text-muted)]">No unsent drafts</p>
              </div>
            ) : (
              <div className="space-y-3">
                {unsentDrafts.map(renderDraftItem)}
              </div>
            )
          ) : scheduledDrafts.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
                <AppIcon name="ClockIcon" size={28} className="text-[var(--text-muted)]" />
              </div>
              <p className="text-[var(--text-muted)]">No scheduled messages</p>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduledDrafts.map(renderDraftItem)}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

DraftsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};
